import React, { Fragment } from "react";
import PropTypes from "prop-types";
import MyErrorBoundary from "../../utils/ErrorBoundary";
import Tooltip from "../../utils/Tooltip";

const labelWrap = {
  display: "inline-block",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden",
  width: "124px",
};
// *******************************************************
// RAIL
// *******************************************************
export function SliderRail({ getRailProps, vertical }) {
  const railOuterStyle = vertical
    ? {
        height: "100%",
        width: 60,
        transform: "translate(-50%, 0%)",
        borderRadius: 7,
        cursor: "pointer",
        marginLeft: 10,
        position: "absolute",
        left: "50%",
        top: 0,
        bottom: 0,
      }
    : {
        width: "100%",
        height: 50,
        transform: "translate(0%, -50%)",
        borderRadius: 7,
        cursor: "pointer",
        marginTop: 10,
      };

  const railInnerStyle = vertical
    ? {
        position: "absolute",
        top: 0,
        bottom: 0,
        height: "100%",
        width: 60,
        transform: "translate(-50%, 0%)",
        borderRadius: 7,
        pointerEvents: "none",
        backgroundColor: "var(--se-sld-circline)",
        left: "50%",
        // outline: '1px dashed #ccc', // debug
      }
    : {
        position: "absolute",
        width: "100%",
        height: 40,
        transform: "translate(0%, -50%)",
        borderRadius: 7,
        pointerEvents: "none",
        backgroundColor: "var(--se-sld-circline)",
      };

  return (
    <Fragment>
      <div style={railOuterStyle} {...getRailProps()} />
      <div style={railInnerStyle} />
    </Fragment>
  );
}

SliderRail.propTypes = {
  getRailProps: PropTypes.func.isRequired,
  vertical: PropTypes.bool,
};

SliderRail.defaultProps = {
  vertical: false,
};

// *******************************************************
// HANDLE COMPONENT
// *******************************************************
export function Handle({
  domain: [min, max],
  handle: { id, value, percent },
  disabled,
  getHandleProps,
}) {
  return (
    <MyErrorBoundary>
      <Fragment>
        <div
          style={{
            left: `${percent}%`,
            position: "absolute",
            transform: "translate(-50%, -50%)",
            WebkitTapHighlightColor: "rgba(0,0,0,0)",
            zIndex: 5,
            width: 28,
            height: 42,
            cursor: "pointer",
            // border: '1px solid white',
            backgroundColor: "none",
          }}
          {...getHandleProps(id)}
        />
        <div
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          style={{
            left: `${percent}%`,
            position: "absolute",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            width: 24,
            height: 24,
            borderRadius: "50%",
            boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.3)",
            backgroundColor: disabled ? "#666" : "#ffc400",
          }}
        />
      </Fragment>
    </MyErrorBoundary>
  );
}

Handle.propTypes = {
  domain: PropTypes.array.isRequired,
  handle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
  }).isRequired,
  getHandleProps: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

Handle.defaultProps = {
  disabled: true,
};

// *******************************************************
// KEYBOARD HANDLE COMPONENT
// Uses a button to allow keyboard events
// *******************************************************
export function KeyboardHandle({
  domain: [min, max],
  handle: { id, value, percent },
  disabled,
  getHandleProps,
  values,
  index,
  vertical,
  isEnergyReserveSelectd,
}) {
  let properValue = "";
  properValue = values[index];
  return (
    <MyErrorBoundary>
      <div
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        style={
          vertical
            ? {
                // Direct mapping: percent -> distance from top. Dragging down increases percent and moves handle down.
                top: `${percent}%`,
                position: "absolute",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
                backgroundColor: "var(--se-background-alternative)",
                borderRadius: "100%",
                width: 16,
                height: 16,
                cursor: "pointer",
                left: "50%",
              }
            : {
                left: `${percent}%`,
                position: "absolute",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
                backgroundColor: "var(--se-background-alternative)",
                borderRadius: "100%",
                width: 16,
                height: 16,
                cursor: "pointer",
              }
        }
        {...getHandleProps(id)}
      >
        {!vertical && (
          <div
            style={{
              borderLeft: "1px solid var(--se-background-alternative)",
              height: 40,
              marginTop: -12,
              marginLeft: 8,
              zIndex: 1,
            }}
          ></div>
        )}

        {!vertical && (
          <>
            {isEnergyReserveSelectd ? (
              <div
                style={
                  id === "$$-0"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -77,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-1"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -100,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-2"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -68,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-3"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -100,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-4"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -68,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-5"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -130,
                        textAlign: "left",
                        left: `${
                          percent === 100
                            ? "-41px"
                            : percent === 99
                            ? "-200%"
                            : `${percent}%`
                        }`,
                        width: "90px",
                      }
                    : ""
                }
              >
                {(id === "$$-2" || id === "$$-3" || id === "$$-4") && (
                  <>
                    <span
                     title={`${id === "$$-2" ? "SoC Essential Loadshedding" : id === "$$-3" ? "SoC Non Essential Loadshedding" :"SoC Non Essential Load Reconnection"}`}
                      style={
                        (id === "$$-2" || id === "$$-3" || id === "$$-4") &&
                        100 - percent < 10
                          ?{ ...labelWrap, width:`50px`,}
                          : {}
                      }
                    >
                      {id === "$$-2" ? "SoC Essential Loadshedding" : ""}
                      {id === "$$-3" ? "SoC Non Essential Loadshedding" : ""}
                      {id === "$$-4" ? "SoC Non Essential Load Reconnection" : ""}
                    </span>
                  </>
                )}

                <div
                  style={
                    id === "$$-0" || id === "$$-1" || id === "$$-5"
                      ? {fontSize:"10px"}
                      : { display: "none" }
                  }
                >
                  {id === "$$-0"
                    ? "SoC Min Manufacturer"
                    : id === "$$-1"
                    ? "SoC Min Optimization"
                    : "SoC Max Manufacturer"}
                </div>
              </div>
            ) : (
              <div
                style={
                  id === "$$-0"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -77,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-1"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -100,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-2"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -72,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-3"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -100,
                        textAlign: "left",
                        left: `${percent}%`,
                        width: "90px",
                      }
                    : id === "$$-4"
                    ? {
                        position: "absolute",
                        fontSize: 10,
                        marginTop: -77,
                        textAlign: "left",
                        left: `${
                          percent === 100
                            ? "-41px"
                            : percent === 99
                            ? "-200%"
                            : `${percent}%`
                        }`,
                        width: "90px",
                      }
                    : ""
                }
              >
            {  (id === "$$-1"  || id === "$$-2" || id === "$$-3"  ) &&  <span title={`${id === "$$-1" ? "SoC Essential Loadshedding" : id === "$$-2" ? "SoC Non Essential Loadshedding" :"SoC Non Essential Load Reconnection"}`}>
                  {id === "$$-1" ? "SoC Essential Loadshedding" : ""}
                  {id === "$$-2" ? "SoC Non Essential Loadshedding" : ""}
                  {id === "$$-3" ? "SoC Non Essential Load Reconnection" : ""}
                </span>}

                <div
                  style={
                    id === "$$-0" || id === "$$-4" ? {fontSize:"10px"} : { display: "none" }
                  }
                >
                  {id === "$$-0"
                    ? "SoC Min Manufacturer"
                    : "SoC Max Manufacturer"}
                </div>
              </div>
            )}
          </>
        )}

        {vertical ? (
          <>
            {/* Percentage (left side) */}
            <div
              style={{
                position: "absolute",
                right: 30 + 40 + 10, // approximate space to push to left; adjust if needed
                left: -80,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 10,
                textAlign: "right",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
                pointerEvents: "none",
                width: 50,
              }}
            >
              {properValue}%
            </div>
            {/* Label (right side) */}
            {isEnergyReserveSelectd ? (
              <div
                style={{
                  position: "absolute",
                  left: 50,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 10,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                  pointerEvents: "none",
                }}
              >
                {id === "$$-0" && "SoC Min Manufacturer"}
                 {id === "$$-1" && "SoC Min Optimization"}
                {id === "$$-2" && "SoC Essential Loadshedding"}
                {id === "$$-3" && "SoC Non Essential Loadshedding"}
                {id === "$$-4" && "SoC Non Essential Load Reconnection"}
                {id === "$$-5" && "SoC Max Manufacturer"}
              </div>
            ) : (
              <div
                style={{
                  position: "absolute",
                  left: 50,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 10,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                  pointerEvents: "none",
                }}
              >
                {id === "$$-0" && "SoC Min Manufacturer"}
                {id === "$$-1" && "SoC Essential Loadshedding"}
                {id === "$$-2" && "SoC Non Essential Loadshedding"}
                {id === "$$-3" && "SoC Non Essential Load Reconnection"}
                {id === "$$-4" && "SoC Max Manufacturer"}
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              position: "absolute",
              fontSize: 10,
              marginTop: 10,
              textAlign: "left",
              // left: percent === 100 ? "-19px" : `${percent}%`,
              width: "90px",
            }}
          >
            {properValue}%
          </div>
        )}
      </div>
    </MyErrorBoundary>
  );
}

KeyboardHandle.propTypes = {
  domain: PropTypes.array.isRequired,
  handle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
  }).isRequired,
  getHandleProps: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  vertical: PropTypes.bool,
  isEnergyReserveSelectd: PropTypes.bool,

};

KeyboardHandle.defaultProps = {
  disabled: true,
  vertical: false,
};

// *******************************************************
// TRACK COMPONENT
// *******************************************************
export function Track({
  source,
  target,
  getTrackProps,
  disabled,
  vertical,
  isEnergyReserveSelectd,
}) {
   return (
    <MyErrorBoundary>
      <div
        style={
          vertical
            ? isEnergyReserveSelectd
            ? 
            {
                position: "absolute",
                transform: "translate(0%, -50%)",
                height: 40,
                zIndex: 1,
                backgroundColor: disabled
                  ? "#999"
                  : source.id === "$$-0"
                  ? "#DC0A0A"
                  : source.id === "$$-1"
                  ? "#55453fff"
                  : source.id === "$$-2"
                  ? "#E47F00"
                  : source.id === "$$-3"
                  ? "#9FA0A4"
                  : source.id === "$$-4"
                  ? "#CBCBCB"
                  : "var(--se-sld-circline)",
                borderRadius: 0,
                cursor: "pointer",
                left: `${source.percent}%`,
                width: `${target.percent - source.percent}%`,
              }
            : {
                position: "absolute",
                width: 60,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1,
                backgroundColor: disabled
                  ? "#999"
                  : source.id === "$$-0"
                  ? "#DC0A0A"
                  : source.id === "$$-1"
                  ? "#E47F00"
                  : source.id === "$$-2"
                  ? "#9FA0A4"
                  : source.id === "$$-3"
                  ? "#CBCBCB"
                  : "var(--se-sld-circline)",
                cursor: "pointer",
                top: `${source.percent}%`,
                height: `${target.percent - source.percent}%`,
              }
            : isEnergyReserveSelectd
            ? {
                position: "absolute",
                transform: "translate(0%, -50%)",
                height: 40,
                zIndex: 1,
                backgroundColor: disabled
                  ? "#999"
                  : source.id === "$$-0"
                  ? "#60a917"
                  : source.id === "$$-1"
                  ? "#DC0A0A"
                  : source.id === "$$-2"
                  ? "#55453fff"
                  : source.id === "$$-3"
                  ? "#E47F00"
                  : source.id === "$$-4"
                  ? "#9FA0A4"
                  : "var(--se-sld-circline)",
                borderRadius: 0,
                cursor: "pointer",
                left: `${source.percent}%`,
                width: `${target.percent - source.percent}%`,
              }
            : {
                position: "absolute",
                transform: "translate(0%, -50%)",
                height: 40,
                zIndex: 1,
                backgroundColor: disabled
                  ? "#999"
                  : source.id === "$$-0"
                  ? "#DC0A0A"
                  : source.id === "$$-2"
                  ? "#E47F00"
                  : source.id === "$$-3"
                  ? "#9FA0A4"
                  : source.id === "$$-4"
                  ? "#CBCBCB"
                  : "var(--se-sld-circline)",
                borderRadius: 0,
                cursor: "pointer",
                left: `${source.percent}%`,
                width: `${target.percent - source.percent}%`,
              }
        }
        {...getTrackProps()}
      />
    </MyErrorBoundary>
  );
}

Track.propTypes = {
  source: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
  }).isRequired,
  target: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
  }).isRequired,
  getTrackProps: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  vertical: PropTypes.bool,
  isEnergyReserveSelectd: PropTypes.bool,
};

Track.defaultProps = {
  disabled: false,
  vertical: false,
};

// *******************************************************
// TICK COMPONENT
// *******************************************************
export function Tick({ tick, count, format }) {
  return (
    <div>
      {/* <div
        style={{
          position: 'absolute',
          marginTop: 14,
          width: 1,
          height: 5,
          backgroundColor: 'rgb(200,200,200)',
          left: `${tick.percent}%`,
        }}
      /> */}
      {/* <div
        style={{
          position: 'absolute',
          marginTop: 22,
          fontSize: 10,
          textAlign: 'center',
          marginLeft: `${-(100 / count) / 2}%`,
          width: `${100 / count}%`,
          left: `${tick.percent}%`,
        }}
      >
        {format(tick.value)}-{count}
      </div> */}
    </div>
  );
}

Tick.propTypes = {
  tick: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
  }).isRequired,
  count: PropTypes.number.isRequired,
  format: PropTypes.func.isRequired,
};

// Tick.defaultProps = {
//   format: d => d,
// };
